import { useState, useEffect } from "react";
import { v4 } from "uuid";

import { MetadataItem } from "../types";

import { Metadata } from "@/types";
import { dictToArrayOfDicts, formatJson, arrayOfDictsToDict } from "@/utils/utils";

const useMetadataManager = (initialMetadata: Metadata, onUpdate?: (metadata: Metadata) => void) => {
  const [metadataArray, setMetadataArray] = useState<MetadataItem[]>([]);
  const [jsonToggleByIndex, setJsonToggleByIndex] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    // Filter out the 'model' key from initial metadata
    const filteredMetadata = { ...initialMetadata };
    delete filteredMetadata.model;

    const arrayWithIds = dictToArrayOfDicts(filteredMetadata).map(item => ({
      ...item,
      id: v4(),
    }));
    setMetadataArray(arrayWithIds);
  }, [initialMetadata]);

  const handleInputChange = (id: string, key: string, value: string) => {
    const newData = metadataArray.map(item => (item.id === id ? { ...item, key, value } : item));
    setMetadataArray(newData);
    setSaveSuccess(false);
  };

  const addNewRow = () => {
    setMetadataArray([...metadataArray, { key: "", value: "", id: v4() }]);
    setSaveSuccess(false);
  };

  const deleteRow = (id: string) => {
    const newMetadata = metadataArray.filter(item => item.id !== id);
    setMetadataArray(newMetadata);
    setSaveSuccess(false);
  };

  const toggleJsonEditor = (id: string) => {
    if (jsonToggleByIndex[id]) {
      setJsonToggleByIndex({
        ...jsonToggleByIndex,
        [id]: false,
      });
    } else {
      const foundItem = metadataArray.find(metaItem => metaItem.id === id);
      if (foundItem) {
        handleInputChange(id, foundItem.key, formatJson(foundItem.value));
        setJsonToggleByIndex({
          ...jsonToggleByIndex,
          [id]: true,
        });
      }
    }
  };

  const validateAndSave = () => {
    const keys = metadataArray.map(item => item.key);
    const hasEmptyKey = metadataArray.some(item => item.key === "");
    const hasDuplicateKeys = keys.some((key, index) => key !== "" && keys.indexOf(key) !== index);

    if (metadataArray.some(item => item.key === "model")) {
      setError("'model' is a reserved keyword and cannot be used.");
      setSaveSuccess(false);
      return false;
    } else if (hasEmptyKey) {
      setError("Please fill in all key names before updating.");
      setSaveSuccess(false);
      return false;
    } else if (hasDuplicateKeys) {
      setError("Each key must be unique. Please remove duplicate keys.");
      setSaveSuccess(false);
      return false;
    }
    setError(null);
    const validEntries = metadataArray.filter(item => item.key !== "");
    const finalMetadata = arrayOfDictsToDict(validEntries);

    delete finalMetadata.model;
    if (onUpdate) {
      onUpdate(finalMetadata);
    }
    setSaveSuccess(true);

    setTimeout(() => {
      setSaveSuccess(false);
    }, 3000);

    return true;
  };

  return {
    metadataArray,
    jsonToggleByIndex,
    error,
    saveSuccess,
    handleInputChange,
    addNewRow,
    deleteRow,
    toggleJsonEditor,
    validateAndSave,
  };
};

export default useMetadataManager;
